import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckSquare, Square, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DetailField } from "@/components/common/DetailField";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { RequestStatus } from "@/types/request";
import {
  BillingPaymentMethod,
  BillingPaymentStatus,
} from "@/types/billing-transactions";
import {
  getAllowedStatusesForTask,
  getStatusLabel,
} from "@/constants/requestConstants";
import {
  useRequest,
  useUpdateRequest,
} from "@/components/pages/requests/hooks/useRequests";
import { useTranslation } from "react-i18next";
import { MainGuestCell } from "@/components/pages/settlement/components/table-cells/MainGuestCell";
import { DateCell } from "@/components/pages/settlement/components/table-cells/DateCell";
import ActivityBlock from "@/components/pages/requests/components/activity/ActivityBlock";
import { useUpdateTask } from "@/components/pages/requests/hooks/tasks.hooks";
import AssignedStaffCell from "@/components/common/AssignedStaffCell";
import { getTaskAssignment } from "./utils/taskAssignment";
import {
  getEffectiveRequestPaymentStatus,
  isRequestPaymentRequired,
} from "./utils/paymentStatus";
import { MarkOfflinePaymentButton } from "./components/MarkOfflinePaymentButton";
import {
  getRoomUpgradeNumbers,
  isRoomUpgradeRequest,
} from "./utils/roomUpgradeRequest";
import { getRequestDisplayTitle } from "./utils/requestDisplay";
import { isRequestOverdue } from "./utils/requestDelay";
import { RequestDelayBadge } from "./components/RequestDelayBadge";

export const RequestDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: request, isLoading, error } = useRequest(id!);
  const task = request?.tasks?.[0];
  const {
    assignedTeamId,
    assignedTeamName,
    assignedStaffId,
    assignedStaffName,
  } = getTaskAssignment(task, request?.service);
  const updateRequestMutation = useUpdateRequest();
  const updateTaskMutation = useUpdateTask();

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-muted-foreground">
          {t("requests.page.loadingRequest")}
        </div>
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-red-600">
          {t("requests.page.errorLoadingRequest")}
        </div>
      </div>
    );
  }

  const handleUpdatePriority = async () => {
    try {
      await updateRequestMutation.mutateAsync({
        id: request.id || "",
        data: { priority: !request.priority },
      });
    } catch (error) {
      console.error(t("requests.page.failedToUpdatePriority"), error);
    }
  };

  const handleUpdateStatus = async (newStatus: string) => {
    if (!task?.id) return;
    try {
      await updateTaskMutation.mutateAsync({
        id: task?.id,
        data: { status: newStatus as RequestStatus },
      });
    } catch (error) {
      console.error(t("requests.page.failedToUpdateStatus"), error);
    }
  };

  const status = task?.status as string;
  const allowedStatuses = getAllowedStatusesForTask(status);
  const currentStatusLabel = getStatusLabel(status as RequestStatus);
  const paymentRequired = isRequestPaymentRequired(request);
  const paymentStatus =
    getEffectiveRequestPaymentStatus(request) ||
    (paymentRequired
      ? BillingPaymentStatus.PENDING
      : BillingPaymentStatus.NOT_REQUIRED);
  const paymentMethod = request.paymentMethod;
  const roomUpgradeRequest = request.bookingRoomUpgradeRequest;
  const isUpgradeRequest = isRoomUpgradeRequest(request);
  const { currentRoomNumber, requestedRoomNumber } =
    getRoomUpgradeNumbers(request);
  const requestTitle = getRequestDisplayTitle(
    request,
    t("requests.form.noService"),
  );
  const isOverdue = isRequestOverdue(request);

  const getPaymentStatusLabel = (status: BillingPaymentStatus) => {
    if (status === BillingPaymentStatus.SUCCEEDED) {
      return t("billing.statusSucceeded");
    }
    if (status === BillingPaymentStatus.NOT_REQUIRED) {
      return t("billing.statusNotRequired");
    }
    if (status === BillingPaymentStatus.FAILED) {
      return t("billing.failed");
    }
    if (status === BillingPaymentStatus.REFUNDED) {
      return t("billing.statusRefunded");
    }
    if (status === BillingPaymentStatus.PARTIALLY_REFUNDED) {
      return t("billing.statusPartiallyRefunded");
    }
    return t("billing.statusNotPaid");
  };

  const getPaymentMethodLabel = (method?: BillingPaymentMethod | null) => {
    if (method === BillingPaymentMethod.ONLINE) {
      return t("billing.paymentMethodOnline");
    }
    if (method === BillingPaymentMethod.OFFLINE) {
      return t("billing.paymentMethodOffline");
    }
    return t("requests.details.na");
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              navigate({
                pathname: "/requests",
                search: location.search,
              })
            }
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("requests.page.back")}
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{t("requests.page.request")}</h1>
            <p className="text-muted-foreground">{requestTitle}</p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6">
        <div className="flex flex-col gap-6 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{t("requests.details.requestInformation")}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <DetailField label={t("requests.details.priority")}>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleUpdatePriority}
                      className="!p-0"
                    >
                      <Star
                        className={`h-5 w-5 ${
                          request.priority
                            ? "fill-black text-black"
                            : "fill-transparent text-gray-300"
                        }`}
                      />
                    </Button>
                    <span className="text-muted-foreground text-sm">
                      {request.priority
                        ? t("requests.priority.high")
                        : t("requests.priority.normal")}
                    </span>
                  </div>
                </DetailField>
                <DetailField label={t("requests.details.status")}>
                  <Select value={status} onValueChange={handleUpdateStatus}>
                    <SelectTrigger className="h-8 w-[150px]">
                      {currentStatusLabel}
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={status} disabled>
                        {currentStatusLabel}
                      </SelectItem>
                      {allowedStatuses.map((statusOption) => (
                        <SelectItem
                          key={statusOption.value}
                          value={statusOption.value}
                        >
                          {statusOption.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </DetailField>
                {isUpgradeRequest ? (
                  <>
                    {currentRoomNumber && (
                      <DetailField label={t("requests.details.room")}>
                        <span className="text-sm">{currentRoomNumber}</span>
                      </DetailField>
                    )}
                    {requestedRoomNumber && (
                      <DetailField
                        label={t(
                          "requests.details.requestedRoom",
                          "Requested room",
                        )}
                      >
                        <span className="text-sm">{requestedRoomNumber}</span>
                      </DetailField>
                    )}
                    {roomUpgradeRequest?.status && (
                      <DetailField
                        label={t(
                          "requests.details.upgradeStatus",
                          "Upgrade status",
                        )}
                      >
                        <Badge variant="outline">
                          {roomUpgradeRequest.status}
                        </Badge>
                      </DetailField>
                    )}
                  </>
                ) : (
                  request?.booking?.room && (
                    <DetailField label={t("requests.details.room")}>
                      <span className="text-sm">
                        {request.booking.room.number}
                      </span>
                    </DetailField>
                  )
                )}
                <DetailField label={t("requests.details.guest")}>
                  <MainGuestCell
                    id={request.guestId || ""}
                    firstName={request.guest?.profile.firstName}
                    lastName={request.guest?.profile.lastName}
                    nationality={request.guest?.profile.language}
                    className={"justify-start"}
                  />
                </DetailField>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {request.createdAt && (
                  <DetailField label={t("requests.details.creationTime")}>
                    <DateCell
                      className={"w-fit items-start justify-start"}
                      date={new Date(request.createdAt)}
                    ></DateCell>
                  </DetailField>
                )}
                {request.dueDateTime && (
                  <DetailField label={t("requests.details.dueDate")}>
                    <div className="flex flex-col items-start gap-1">
                      <DateCell
                        className={"w-fit items-start justify-start"}
                        date={new Date(request.dueDateTime)}
                      ></DateCell>
                      {isOverdue ? <RequestDelayBadge /> : null}
                    </div>
                  </DetailField>
                )}
              </div>
              {!!task?.id && (
                <div className="grid grid-cols-2 gap-4">
                  <DetailField label={t("requests.details.assignedStaff")}>
                    <AssignedStaffCell
                      className={"flex-row gap-3"}
                      taskId={task?.id}
                      teamId={assignedTeamId}
                      staffId={assignedStaffId}
                      teamName={assignedTeamName}
                      staffName={assignedStaffName}
                    />
                  </DetailField>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <DetailField label={t("requests.details.paymentStatus")}>
                  <Badge variant="outline">
                    {getPaymentStatusLabel(paymentStatus)}
                  </Badge>
                </DetailField>
                <DetailField label={t("requests.details.paymentMethod")}>
                  <span className="text-sm">
                    {getPaymentMethodLabel(paymentMethod)}
                  </span>
                </DetailField>
              </div>
              <MarkOfflinePaymentButton request={request} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>{t("requests.details.details")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <DetailField label={t("requests.details.service")}>
                  <span className="text-sm">
                    {request.service?.name ?? t("requests.form.noService")}
                  </span>
                </DetailField>
                <DetailField label={t("requests.details.amount")}>
                  <span className="text-sm">{request.quantity}</span>
                </DetailField>
                <DetailField label={t("requests.form.requestDetails")}>
                  <span className="text-sm">
                    {request.description || t("requests.details.na")}
                  </span>
                </DetailField>

                <DetailField label={t("requests.details.notes")}>
                  <span className="text-sm">
                    {request.notes || t("requests.details.na")}
                  </span>
                </DetailField>
              </div>
            </CardContent>
          </Card>
          {!!request.checklistItems?.length && (
            <Card>
              <CardHeader>
                <CardTitle>{t("requests.details.checklist")}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {request.checklistItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-start justify-between gap-4"
                    >
                      <div className="flex min-w-0 items-start gap-3">
                        {item.isChecked ? (
                          <CheckSquare className="mt-0.5 h-5 w-5 shrink-0 text-black" />
                        ) : (
                          <Square className="mt-0.5 h-5 w-5 shrink-0 text-black" />
                        )}
                        <div className="min-w-0 text-sm">{item.title}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      <ActivityBlock requestId={id!} />
    </div>
  );
};
